/* raw-ok: SVG grid/stroke spec in prose, not a CSS literal — 24px grid, 2px stroke, currentColor (§ art bible 10.4) */

export const WorldGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
    <path
      d="M4 12h16M12 4c2.5 2.2 4 5 4 8s-1.5 5.8-4 8c-2.5-2.2-4-5-4-8s1.5-5.8 4-8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const HabitatGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path
      d="M4 11 12 4l8 7v8a1 1 0 0 1-1 1h-5v-5h-4v5H5a1 1 0 0 1-1-1z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const WardrobeGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path
      d="M12 3a2 2 0 0 1 2 2 2 2 0 0 0 2 2h.5L21 12H15v6h-6v-6H3l4.5-5H8a2 2 0 0 0 2-2 2 2 0 0 1 2-2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

// #14.1 (CLAUDE.md "Hide the empty"): the dock's Wardrobe slot while `wardrobe` is off. A ribbon
// bookmark on the same 24-unit grid / 2-unit stroke / currentColor convention as every glyph
// above, reused nowhere else, so it stays the Journal's own mark if a future icon set adds a real
// wardrobe garment glyph beside it.
export const JournalGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path
      d="M5 4.5A1.5 1.5 0 0 1 6.5 3h11A1.5 1.5 0 0 1 19 4.5v16l-3.5-2.5-3.5 2.5-3.5-2.5L5 20.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M8.5 8h7M8.5 11.5h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const BagGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path
      d="M8 9V7a4 4 0 0 1 8 0v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M5 9h14l-1.2 10.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const MeGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
    <path
      d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// Task 17 (Look gallery + profile favourite toggle, SCREENS.md "/looks/gallery"/"/profile"):
// §11.8 "never colour alone" — the fill/outline distinction below is paired with the button's
// own aria-pressed state and accessible label (ProfileScreen/LookGalleryScreen), never relied on
// by itself. `fill="currentColor"` (filled) vs `fill="none"` + stroke (outline) so a single
// `color` CSS property on the parent button drives both states with no per-state hex literal.
// Task 15 (Shells HUD): a ribbed clam-shell silhouette, same 24-unit grid/2-unit stroke/
// currentColor convention as every glyph above (raw-ok: see the file's own top-of-file comment
// for the spec this mirrors). Kept outline-only (no fill) so it reads correctly against both the
// HUD's own background and any future theme, exactly like WorldGlyph/HabitatGlyph.
export const ShellGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path
      d="M12 4c4 2 7 6.5 7 11a7 7 0 0 1-14 0c0-4.5 3-9 7-11z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M12 8v9M9 10.5 12 13l3-2.5M8 13.5 12 16l4-2.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Task 22 (§8.7 badge wall): no badge art exists yet (task-22-brief.md) — this engine-drawn
// rosette (medallion + ribbon tails) stands in for every badge tile until real art lands, same
// 24-unit grid/2-unit stroke/currentColor convention as every glyph above. Always aria-hidden —
// BadgeTile pairs it with real visible text (name or hint), never relies on the glyph alone.
export const RosetteGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <circle cx="12" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9 14.3 6.5 21l5.5-2.6 5.5 2.6-2.5-6.7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M12 6.5v5M9.5 9h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const HeartGlyph = ({ filled }: { filled: boolean }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M12 20.5s-7.5-4.6-10-9.3C.4 8 2 4.5 5.5 4c2.1-.3 4 .8 6.5 3.2C14.5 4.8 16.4 3.7 18.5 4c3.5.5 5.1 4 3.5 7.2-2.5 4.7-10 9.3-10 9.3z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

// Ability info affordance (SCREENS.md "/world/training — Abilities tab" -> `info`). Same 24-unit
// grid / 2-unit stroke / currentColor convention as every glyph above, so it inherits the row's
// own colour rather than introducing one. Always aria-hidden: the button that wraps it carries the
// real accessible name ("About Overclock"), so a screen reader announces the ability, not "info".
export const InfoGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 11v5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="7.75" r="1.25" fill="currentColor" />
  </svg>
);

// Skill-craft UI polish (spec §3.3): the `🔒 Lv n` chip on a skill-locked `SkillActionRow`. A
// padlock on the same 24-unit grid / 2-unit stroke / currentColor convention as every glyph
// above, so it takes the chip's own quiet colour. Always aria-hidden — the chip's text ("Lv 10")
// carries the meaning; the lock only says it faster.
export const LockGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8 10.5V7.5a4 4 0 0 1 8 0v3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.5" r="1.25" fill="currentColor" />
  </svg>
);
